/**
 * Chat Service
 * Service layer for AI chat functionality
 *
 * Handles communication with the FastAPI chat endpoint:
 * POST /api/v1/chat/query
 *
 * Architecture:
 * - Uses Axios for standard HTTP requests
 * - Uses Fetch API for SSE streaming
 * - Includes authentication token via interceptors/manual headers
 * - Proper timeout configuration for slow APIs
 * - Type-safe request/response handling
 *
 * @module chat.service
 */
 
import { AxiosInstance, AxiosResponse } from 'axios';
import { axiosInstance } from '@services/api/axios.instance';
import { API_TIMEOUT } from '@core/constants/app.constants';
import { useAuthStore } from '@store/auth.store';
import { refreshAccessToken, isTokenExpiringSoon, isTokenExpired } from '@services/api/interceptors';
import { enqueueSnackbar } from 'notistack';
import { getFastAPIUrl } from '@core/utils/env.utils';
 
/**
 * Chat query request payload
 */
export interface ChatQueryRequest {
  query: string;
}
 
/**
 * New session request payload
 */
export interface NewSessionRequest {
  title: string;
}
 
/**
 * New session response
 */
export interface NewSessionResponse {
  session_id?: string;
  message?: string;
  [key: string]: any;
}
 
/**
 * Chat message from session
 */
export interface SessionMessage {
  id: string;
  session_id: string;
  role: 'user' | 'assistant';
  content: string;
  metadata?: any;
  activity_id?: string | null;
  created_at: string;
}
 
/**
 * Active session response
 */
export interface ActiveSessionResponse {
  id?: string;
  user_id?: string;
  title?: string;
  is_active?: boolean;
  agent_type?: string;
  external_conversation_id?: string | null;
  messages?: SessionMessage[];
  created_at?: string;
  updated_at?: string;
}
 
/**
 * Chat query response structure
 * The API response can be in various formats (text, structured data, etc.)
 * We'll handle it as a flexible structure
 */
export interface ChatQueryResponse {
  response?: string;
  content?: string;
  message?: string;
  data?: any;
  // Structured data fields
  description?: string;
  categories?: any;
  tools?: any;
  'services/technology_areas'?: any;
  services?: any;
  service_technology_area?: string; // Service/Technology area from backend done event
  has_results?: boolean;
  source_count?: number;
  additional_sources_used?: boolean; // True when response was generated from Additional Sources
  additional_sources_content?: string; // document_content from first Additional Source
  [key: string]: any; // Allow for flexible response structure
}
 
/**
 * Web search result item
 */
export interface WebSearchResult {
  title: string;
  url: string;
  snippet: string;
  display_url: string;
}
 
/**
 * SSE Event types from streaming API
 */
export interface SSETokenEvent {
  type: 'token';
  content: string;
}
 
export interface SSEDoneEvent {
  type: 'done';
  response?: string; // Full LLM-generated text from backend
  service_technology_area?: string; // Service/Technology area from backend
  categories?: any; // Optional - may not be present if has_results is false
  has_results: boolean;
  source_count: number;
  additional_sources_used?: boolean; // True when response was generated from Additional Sources content
  additional_sources_content?: string; // document_content from first Additional Source
}
 
export interface SSEWebSearchPromptEvent {
  type: 'web_search_prompt';
  message: string;
  query: string;
  actions: string[];
  requires_user_action: boolean;
}
 
export interface SSEWebResultsEvent {
  type: 'web_results';
  results: WebSearchResult[];
  source: string;
  query: string;
  result_count: number;
  success: boolean;
}
 
export type SSEEvent = SSETokenEvent | SSEDoneEvent | SSEWebSearchPromptEvent | SSEWebResultsEvent;
 
/**
 * Streaming callbacks
 */
export interface StreamCallbacks {
  onToken: (token: string) => void;
  onDone: (data: ChatQueryResponse) => void;
  onWebSearchPrompt?: (event: SSEWebSearchPromptEvent) => void;
  onWebResults?: (event: SSEWebResultsEvent) => void;
  onError: (error: Error) => void;
}
 
/**
 * Chat Service Class
 * Provides methods for interacting with the chat API
 */
class ChatService {
  private readonly baseUrl: string;
  private readonly axios: AxiosInstance;
 
  constructor() {
    // Use FastAPI base URL (same pattern as proxy service)
    this.baseUrl = getFastAPIUrl();
    this.axios = axiosInstance;
  }
 
  /**
   * Get auth token from store (same source as Axios interceptors)
   */
  private getAuthToken(): string | null {
    return useAuthStore.getState()?.accessToken || null;
  }
 
  /**
   * Send a chat query with SSE streaming support
   *
   * @param query - User's chat message/query
   * @param callbacks - Callbacks for token, done, and error events
   * @param abortController - Optional AbortController for cancellation
   * @param isRetry - Internal flag to prevent infinite retry loops
   *
   * Endpoint: POST /api/v1/chat/query
   * Headers:
   *   - Authorization: Bearer <token>
   *   - Content-Type: application/json
   *   - Accept: text/event-stream
   *
   * SSE Events:
   *   - {"type": "token", "content": "word"} - Streaming tokens
   *   - {"type": "done", "categories": {...}} - Final structured data
   */
  async sendQueryStream(
    query: string,
    callbacks: StreamCallbacks,
    abortController?: AbortController,
    isRetry: boolean = false
  ): Promise<void> {
    if (!query || !query.trim()) {
      callbacks.onError(new Error('Query cannot be empty'));
      return;
    }

    // Get current token and proactively refresh if expiring soon
    let token = this.getAuthToken();
    
    // Proactive token refresh: Check if token is expiring soon (within 5 minutes)
    if (token && !isRetry) {
      if (isTokenExpiringSoon(token, 5)) {
        if (import.meta.env.DEV) {
          console.log('🔄 Chat Service: Token expiring soon, refreshing proactively...');
        }
        
        // Attempt proactive refresh
        const newToken = await refreshAccessToken(false);
        if (newToken) {
          token = newToken;
          if (import.meta.env.DEV) {
            console.log('✅ Chat Service: Token refreshed proactively');
          }
        } else if (isTokenExpired(token)) {
          // Token is already expired and refresh failed
          if (import.meta.env.DEV) {
            console.warn('🔒 Chat Service: Token expired and refresh failed');
          }
          // Will be handled by 401 error handling below
        }
      }
    }
    
    const controller = abortController || new AbortController();

    try {
      const response = await fetch(`${this.baseUrl}/api/v1/chat/query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          'X-Request-Time': new Date().toISOString(),
          'X-Client-Version': window._env_.AIPORTAL_APP_VERSION || '1.0.0',
        },
        body: JSON.stringify({ query: query.trim() }),
        signal: controller.signal,
      });
 
      // Handle 401 Unauthorized - try to refresh token and retry
      if (response.status === 401 && !isRetry) {
        if (import.meta.env.DEV) {
          console.log('🔄 Chat Service: 401 Unauthorized - Attempting token refresh...');
        }
 
        // Check if we have a refresh token
        const refreshToken = useAuthStore.getState()?.refreshToken || localStorage.getItem('refreshToken');
       
        if (!refreshToken) {
          // No refresh token available - clear auth and redirect
          if (import.meta.env.DEV) {
            console.warn('🔒 Chat Service: No refresh token available - clearing auth');
          }
         
          useAuthStore.getState()?.clearAuth?.();
         
          // Show notification only if not already on login page
          if (!window.location.pathname.includes('/login')) {
            enqueueSnackbar('Your session token has expired. Please login again.', {
              variant: 'warning',
              autoHideDuration: 4000,
            });
          }
         
          setTimeout(() => {
            window.location.href = '/login';
          }, 500);
         
          callbacks.onError(new Error('Session expired. Please login again.'));
          return;
        }
 
        // Try to refresh the token
        try {
          if (import.meta.env.DEV) {
            console.log('🔄 Chat Service: Attempting token refresh...');
          }
         
          // Use the centralized refreshAccessToken function
          // Force refresh since we got a 401 (token is definitely invalid)
          const newAccessToken = await refreshAccessToken(true);
         
          if (!newAccessToken) {
            throw new Error('Token refresh failed');
          }
 
          if (import.meta.env.DEV) {
            console.log('✅ Chat Service: Token refreshed successfully, retrying request');
          }
         
          // Retry the request with new token (recursive call with isRetry flag)
          return this.sendQueryStream(query, callbacks, abortController, true);
         
        } catch (refreshError: any) {
          // Refresh failed - clear auth and redirect
          if (import.meta.env.DEV) {
            console.error('❌ Chat Service: Token refresh failed:', refreshError.message);
          }
         
          useAuthStore.getState()?.clearAuth?.();
         
          // Show notification only if not already on login page
          if (!window.location.pathname.includes('/login')) {
            enqueueSnackbar('Your session token has expired. Please login again.', {
              variant: 'warning',
              autoHideDuration: 4000,
            });
          }
         
          setTimeout(() => {
            window.location.href = '/login';
          }, 500);
         
          callbacks.onError(refreshError instanceof Error ? refreshError : new Error(String(refreshError)));
          return;
        }
      }
 
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
 
      if (!response.body) {
        throw new Error('Response body is null');
      }
 
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let doneReceived = false;
      let doneEventData: ChatQueryResponse | null = null;
 
      if (import.meta.env.DEV) {
        console.log('🔄 SSE Stream started for query:', query.substring(0, 50));
      }
 
      while (true) {
        const { done, value } = await reader.read();
       
        if (done) {
          // Process any remaining data in buffer before finishing
          if (buffer.trim()) {
            const lines = buffer.split('\n');
            for (const line of lines) {
              const trimmedLine = line.trim();
              if (trimmedLine && !trimmedLine.startsWith(':') && trimmedLine.startsWith('data: ')) {
                const jsonStr = trimmedLine.substring(6);
                try {
                  const event: SSEEvent = JSON.parse(jsonStr);
                  this._processSSEEvent(event, callbacks);
                  if (event.type === 'done') {
                    doneEventData = {
                      response: event.response || undefined,
                      service_technology_area: event.service_technology_area || undefined,
                      categories: event.categories || undefined,
                      has_results: event.has_results,
                      source_count: event.source_count,
                      additional_sources_used: (event as SSEDoneEvent).additional_sources_used || false,
                      additional_sources_content: (event as SSEDoneEvent).additional_sources_content || undefined,
                    };
                    doneReceived = true;
                  }
                } catch (parseError) {
                  if (import.meta.env.DEV) {
                    console.warn('⚠️ Failed to parse SSE event:', jsonStr, parseError);
                  }
                }
              }
            }
          }
         
          // Call onDone after processing all remaining data
          if (doneReceived && doneEventData) {
            callbacks.onDone(doneEventData);
          } else if (doneReceived === false) {
            // Stream ended without done event - call onDone with empty data
            callbacks.onDone({
              has_results: false,
              source_count: 0,
            });
          }
         
          if (import.meta.env.DEV) {
            console.log('✅ SSE Stream completed');
          }
          break;
        }
 
        // Decode chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });
       
        // Process complete lines from buffer
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
 
        for (const line of lines) {
          const trimmedLine = line.trim();
         
          // Skip empty lines and comments
          if (!trimmedLine || trimmedLine.startsWith(':')) {
            continue;
          }
 
          // Parse SSE data line
          if (trimmedLine.startsWith('data: ')) {
            const jsonStr = trimmedLine.substring(6); // Remove 'data: ' prefix
           
            try {
              const event: SSEEvent = JSON.parse(jsonStr);
             
              this._processSSEEvent(event, callbacks);
             
              if (event.type === 'done') {
                // Store done event but don't call onDone yet - wait for stream to finish
                // This ensures all tokens are processed first
                doneEventData = {
                  response: event.response || undefined,
                  service_technology_area: event.service_technology_area || undefined,
                  categories: event.categories || undefined, // Only include if present
                  has_results: event.has_results,
                  source_count: event.source_count,
                  additional_sources_used: event.additional_sources_used || false,
                  additional_sources_content: event.additional_sources_content || undefined,
                };
                doneReceived = true;
              }
            } catch (parseError) {
              if (import.meta.env.DEV) {
                console.warn('⚠️ Failed to parse SSE event:', jsonStr, parseError);
              }
            }
          }
        }
      }
    } catch (error: any) {
      // Handle abort
      if (error.name === 'AbortError') {
        if (import.meta.env.DEV) {
          console.log('🛑 SSE Stream aborted');
        }
        return;
      }
 
      if (import.meta.env.DEV) {
        console.error('❌ SSE Stream Error:', error);
      }
     
      callbacks.onError(error instanceof Error ? error : new Error(String(error)));
    }
  }
 
  /**
   * Process SSE events from streaming response
   * Routes different event types to appropriate callbacks
   */
  private _processSSEEvent(event: SSEEvent, callbacks: StreamCallbacks): void {
    if (event.type === 'token') {
      callbacks.onToken(event.content);
    } else if (event.type === 'done') {
      // Done event is handled separately in sendQueryStream
    } else if (event.type === 'web_search_prompt') {
      if (callbacks.onWebSearchPrompt) {
        callbacks.onWebSearchPrompt(event);
      }
    } else if (event.type === 'web_results') {
      if (callbacks.onWebResults) {
        callbacks.onWebResults(event);
      }
    }
  }
 
  /**
   * Execute web search (called after user confirms on web_search_prompt)
   *
   * @param query - Search query
   * @returns Promise resolving to web search results
   *
   * @throws {AxiosError} If the request fails
   *
   * Endpoint: POST /api/v1/chat/web-search
   * Headers:
   *   - Authorization: Bearer <token> (added by interceptors)
   *   - Content-Type: application/json
   *
   * Request Body:
   *   {
   *     "query": "<search query>"
   *   }
   *
   * Response:
   *   {
   *     "type": "web_results",
   *     "results": [
   *       {
   *         "title": "...",
   *         "url": "...",
   *         "snippet": "...",
   *         "display_url": "..."
   *       }
   *     ],
   *     "source": "web",
   *     "query": "...",
   *     "result_count": number,
   *     "success": true
   *   }
   */
  async webSearch(query: string): Promise<{ results: WebSearchResult[]; query: string; result_count: number }> {
    if (!query || !query.trim()) {
      throw new Error('Search query cannot be empty');
    }
 
    const payload = {
      query: query.trim(),
    };
 
    try {
      const response: AxiosResponse = await this.axios.post(
        `${this.baseUrl}/api/v1/chat/web-search`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          timeout: API_TIMEOUT,
        }
      );
 
      if (import.meta.env.DEV) {
        console.log('🌐 Web Search Results:', {
          status: response.status,
          resultCount: response.data?.result_count,
          query: response.data?.query,
        });
      }
 
      return {
        results: response.data?.results || [],
        query: response.data?.query || query,
        result_count: response.data?.result_count || 0,
      };
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error('❌ Web Search Error:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
        });
      }
      throw error;
    }
  }
 
  /**
   * Send a chat query to the AI endpoint (non-streaming, legacy support)
   *
   * @param query - User's chat message/query
   * @returns Promise resolving to chat response
   *
   * @throws {AxiosError} If the request fails
   *
   * Endpoint: POST /api/v1/chat/query
   * Headers:
   *   - Authorization: Bearer <token> (added by interceptors)
   *   - Content-Type: application/json
   *   - Accept: application/json
   *
   * Request Body:
   *   {
   *     "query": "<user text>"
   *   }
   */
  async sendQuery(query: string): Promise<ChatQueryResponse> {
    if (!query || !query.trim()) {
      throw new Error('Query cannot be empty');
    }
 
    const payload: ChatQueryRequest = {
      query: query.trim(),
    };
 
    try {
      const response: AxiosResponse<ChatQueryResponse> = await this.axios.post(
        `${this.baseUrl}/api/v1/chat/query`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          timeout: API_TIMEOUT, // Use centralized timeout (default: 60s)
        }
      );
 
      if (import.meta.env.DEV) {
        console.log('💬 Chat API Response:', {
          status: response.status,
          data: response.data,
        });
      }
 
      // Return raw response data - parsing will be done in the hook
      return response.data;
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error('❌ Chat API Error:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
        });
      }
      throw error;
    }
  }
 
 
  /**
   * Create a new chat session
   *
   * @param title - Session title (default: "New Chat")
   * @returns Promise resolving to new session response
   *
   * @throws {AxiosError} If the request fails
   *
   * Endpoint: POST /api/v1/chat/sessions/new
   * Headers:
   *   - Authorization: Bearer <token> (added by interceptors)
   *   - Content-Type: application/json
   *   - Accept: application/json
   *
   * Request Body:
   *   {
   *     "title": "New Chat"
   *   }
   */
  async createNewSession(title: string = 'New Chat'): Promise<NewSessionResponse> {
    const payload: NewSessionRequest = {
      title: title.trim() || 'New Chat',
    };
 
    try {
      const response: AxiosResponse<NewSessionResponse> = await this.axios.post(
        `${this.baseUrl}/api/v1/chat/sessions/new`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          timeout: API_TIMEOUT,
        }
      );
 
      if (import.meta.env.DEV) {
        console.log('🆕 New Chat Session Created:', {
          status: response.status,
          data: response.data,
        });
      }
 
      return response.data;
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error('❌ New Session API Error:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
        });
      }
      throw error;
    }
  }
 
  /**
   * Get active chat session
   *
   * @returns Promise resolving to active session with messages, or null if no active session
   *
   * Endpoint: GET /api/v1/chat/sessions/active
   * Headers:
   *   - Authorization: Bearer <token> (added by interceptors)
   *   - Accept: application/json
   *
   * Note: This method gracefully handles errors and returns null instead of throwing.
   * This allows the chat to start fresh if there's any issue with the session service.
   */
  async getActiveSession(
    agentType: 'rag' | 'copilot' = 'rag'
  ): Promise<ActiveSessionResponse | null> {
    try {
      const response: AxiosResponse<ActiveSessionResponse> = await this.axios.get(
        `${this.baseUrl}/api/v1/chat/sessions/active`,
        {
          params: { agent_type: agentType },
          headers: {
            'Accept': 'application/json',
          },
          timeout: API_TIMEOUT,
          // Skip retry and error logging for this endpoint - we handle errors gracefully
          skipRetry: true,
          skipErrorLogging: true,
          skipErrorNotification: true,
        } as any
      );

      if (import.meta.env.DEV) {
        console.log('📋 Active Session Retrieved:', {
          status: response.status,
          agentType,
          hasSession: !!response.data?.id,
          messageCount: response.data?.messages?.length || 0,
        });
      }
 
      // Return null if no active session (404 or empty response)
      if (response.status === 404 || !response.data?.id) {
        return null;
      }
 
      return response.data;
    } catch (error: any) {
      // 404 means no active session - this is expected, not an error
      // 500 means server error - we gracefully handle by returning null
      // This allows chat to start fresh without blocking on database issues
      const status = error.response?.status;
     
      if (status === 404 || status === 500) {
        if (import.meta.env.DEV) {
          console.log(`📋 No active session available (status: ${status || 'network error'})`);
        }
        return null;
      }
 
      // For other errors (network, auth), log in dev only
      if (import.meta.env.DEV) {
        console.log('📋 Session service unavailable, starting fresh chat');
      }
     
      // Return null instead of throwing - this allows graceful fallback
      return null;
    }
  }
 
  /**
   * Decline web search (save user's decision to backend)
   *
   * @param query - The search query that was declined
   * @returns Promise resolving to success response
   *
   * @throws {AxiosError} If the request fails
   *
   * Endpoint: POST /api/v1/chat/web-search/decline
   * Headers:
   *   - Authorization: Bearer <token> (added by interceptors)
   *   - Content-Type: application/json
   *
   * Request Body:
   *   {
   *     "query": "<search query>"
   *   }
   *
   * Response:
   *   {
   *     "message": "Web search declined",
   *     "success": true
   *   }
   */
  async declineWebSearch(query: string): Promise<{ message: string; success: boolean }> {
    if (!query || !query.trim()) {
      throw new Error('Search query cannot be empty');
    }

    const payload = {
      query: query.trim(),
    };

    try {
      const response: AxiosResponse = await this.axios.post(
        `${this.baseUrl}/api/v1/chat/web-search/decline`,
        payload,
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          timeout: API_TIMEOUT,
        }
      );

      if (import.meta.env.DEV) {
        console.log('❌ Web Search Declined:', {
          status: response.status,
          query: response.data?.query,
        });
      }

      return {
        message: response.data?.message || 'Web search declined',
        success: response.data?.success ?? true,
      };
    } catch (error: any) {
      if (import.meta.env.DEV) {
        console.error('❌ Decline Web Search Error:', {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
        });
      }
      throw error;
    }
  }

  /**
   * Get the base URL being used
   */
  getBaseUrl(): string {
    return this.baseUrl;
  }
}
 
// Export singleton instance
export const chatService = new ChatService();
export default chatService;